/**
 * Timezone-Safe Date Utilities for Accounting Dates
 *
 * Accounting dates (invoice_date, rate_date, bill_date) are business facts
 * that should NEVER shift based on the viewer's timezone.
 *
 * Problem: When dayjs parses a date with time info like "2025-09-29T14:00:00.000Z",
 * it converts to local timezone. In Singapore (UTC+8), this becomes 10PM Sep 29,
 * but if the time is 16:00+ UTC, it crosses midnight and becomes Sep 30.
 *
 * Solution: Use dayjs.utc() to parse dates in UTC mode, ensuring the displayed
 * date matches the UTC date regardless of the user's timezone.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(customParseFormat);

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Serialize date-picker values for task due dates as date-only strings.
 *
 * Task due dates are business dates, not timestamps. Using toISOString()
 * can shift a selected local-midnight date to the previous UTC day.
 *
 * @param {string|Date|dayjs.Dayjs|null} value - Date picker or date-like value
 * @returns {string|null} YYYY-MM-DD or null for empty/invalid input
 */
export function serializeTaskDateOnly(value) {
    if (!value) return null;

    if (typeof value === 'string') {
        const text = value.trim();
        if (!text) return null;
        if (DATE_ONLY_RE.test(text)) return text;
    }

    if (typeof value.format === 'function') {
        return value.format('YYYY-MM-DD');
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

/**
 * Serialize a task deadline without discarding its selected time.
 *
 * DatePicker values are interpreted in the user's local timezone and converted
 * to an ISO timestamp for transport. Legacy date-only strings are treated as
 * end-of-day deadlines so older callers do not create tasks that are already
 * overdue at the start of the selected day.
 *
 * @param {string|Date|dayjs.Dayjs|null} value - Date-time picker or date-like value
 * @returns {string|null} ISO timestamp or null for empty/invalid input
 */
export function serializeTaskDateTime(value) {
    if (!value) return null;

    if (typeof value === 'string') {
        const text = value.trim();
        if (!text) return null;
        if (DATE_ONLY_RE.test(text)) {
            return dayjs(text)
                .hour(23)
                .minute(59)
                .second(0)
                .millisecond(0)
                .toISOString();
        }
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.toISOString() : null;
}

/**
 * Format an accounting date - timezone safe
 *
 * Use this for all date-only fields in financial reports:
 * - Invoice dates
 * - Bill dates
 * - Rate dates
 * - Project start/end dates
 *
 * @param {string|Date|null} date - Date to format (can be YYYY-MM-DD or full ISO)
 * @param {string} format - Output format (default: 'DD MMM YYYY')
 * @returns {string} Formatted date string, or '-' if input is falsy
 *
 * @example
 * formatAccountingDate('2025-09-29'); // "29 Sep 2025"
 * formatAccountingDate('2025-09-29T14:00:00.000Z'); // "29 Sep 2025" (not Sep 30!)
 * formatAccountingDate(null); // "-"
 * formatAccountingDate('2025-09-29', 'YYYY-MM-DD'); // "2025-09-29"
 */
export function formatAccountingDate(date, format = 'DD MMM YYYY') {
    if (!date) return '-';

    // Try standard UTC parsing first
    let parsed = dayjs.utc(date);

    // If invalid or if input looks like M/D/YYYY format, try parsing with explicit format
    // This handles legacy dates stored in American format (e.g., "1/1/2020", "5/8/2025")
    if (!parsed.isValid() || (typeof date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date))) {
        parsed = dayjs.utc(date, 'M/D/YYYY');
    }

    // Final fallback - return dash if still invalid
    if (!parsed.isValid()) return '-';

    return parsed.format(format);
}

/**
 * Format accounting date for YYYY-MM-DD (ISO format, no time)
 *
 * Useful for API calls, sorting, and comparisons.
 *
 * @param {string|Date|null} date - Date to format
 * @returns {string|null} Date in YYYY-MM-DD format, or null if input is falsy
 *
 * @example
 * formatAccountingDateISO('2025-09-29T14:00:00.000Z'); // "2025-09-29"
 */
export function formatAccountingDateISO(date) {
    if (!date) return null;

    // Try standard UTC parsing first
    let parsed = dayjs.utc(date);

    // If invalid or if input looks like M/D/YYYY format, try parsing with explicit format
    if (!parsed.isValid() || (typeof date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date))) {
        parsed = dayjs.utc(date, 'M/D/YYYY');
    }

    if (!parsed.isValid()) return null;
    return parsed.format('YYYY-MM-DD');
}

/**
 * Parse a date string as UTC dayjs object
 *
 * Use when you need to perform date calculations while preserving UTC.
 *
 * @param {string|Date|null} date - Date to parse
 * @returns {dayjs.Dayjs|null} dayjs object in UTC mode, or null if input is falsy
 *
 * @example
 * const d = parseAccountingDate('2025-09-29');
 * d.add(1, 'day').format('YYYY-MM-DD'); // "2025-09-30"
 */
export function parseAccountingDate(date) {
    if (!date) return null;

    // Try standard UTC parsing first
    let parsed = dayjs.utc(date);

    // If invalid or if input looks like M/D/YYYY format, try parsing with explicit format
    if (!parsed.isValid() || (typeof date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date))) {
        parsed = dayjs.utc(date, 'M/D/YYYY');
    }

    return parsed.isValid() ? parsed : null;
}

/**
 * Check if two accounting dates are the same (date-only comparison)
 *
 * @param {string|Date|null} date1 - First date
 * @param {string|Date|null} date2 - Second date
 * @returns {boolean} True if dates represent the same calendar day in UTC
 *
 * @example
 * isSameAccountingDate('2025-09-29', '2025-09-29T14:00:00.000Z'); // true
 */
export function isSameAccountingDate(date1, date2) {
    if (!date1 || !date2) return false;
    return dayjs.utc(date1).isSame(dayjs.utc(date2), 'day');
}

/**
 * Get start of month for an accounting date (in UTC)
 *
 * @param {string|Date} date - Date to get month start for
 * @returns {string} First day of the month in YYYY-MM-DD format
 *
 * @example
 * getMonthStartUTC('2025-09-15'); // "2025-09-01"
 */
export function getMonthStartUTC(date) {
    return dayjs.utc(date).startOf('month').format('YYYY-MM-DD');
}

/**
 * Get end of month for an accounting date (in UTC)
 *
 * @param {string|Date} date - Date to get month end for
 * @returns {string} Last day of the month in YYYY-MM-DD format
 *
 * @example
 * getMonthEndUTC('2025-09-15'); // "2025-09-30"
 */
export function getMonthEndUTC(date) {
    return dayjs.utc(date).endOf('month').format('YYYY-MM-DD');
}

/**
 * Compare two accounting dates for sorting
 *
 * Returns a number suitable for Array.sort():
 * - Negative if date1 < date2
 * - Zero if date1 === date2
 * - Positive if date1 > date2
 *
 * @param {string|Date|null} date1 - First date
 * @param {string|Date|null} date2 - Second date
 * @returns {number} Comparison result for sorting
 *
 * @example
 * // Use in table sorter:
 * sorter: (a, b) => compareAccountingDates(a.invoice_date, b.invoice_date)
 *
 * // Use in Array.sort:
 * dates.sort((a, b) => compareAccountingDates(a.date, b.date))
 */
export function compareAccountingDates(date1, date2) {
    // Handle null/undefined - sort nulls to the end
    if (!date1 && !date2) return 0;
    if (!date1) return 1;  // null sorts after valid dates
    if (!date2) return -1; // valid dates sort before null

    const d1 = dayjs.utc(date1);
    const d2 = dayjs.utc(date2);

    if (d1.isBefore(d2)) return -1;
    if (d1.isAfter(d2)) return 1;
    return 0;
}

/**
 * Get month key from accounting date (YYYY-MM format)
 *
 * Useful for grouping data by month in reports.
 *
 * @param {string|Date|null} date - Date to extract month from
 * @returns {string|null} Month key in YYYY-MM format, or null if input is falsy
 *
 * @example
 * getAccountingMonthKey('2025-09-29'); // "2025-09"
 * getAccountingMonthKey('2025-09-29T14:00:00.000Z'); // "2025-09"
 */
export function getAccountingMonthKey(date) {
    if (!date) return null;
    return dayjs.utc(date).format('YYYY-MM');
}

/**
 * Get year and month components from accounting date (in UTC)
 *
 * @param {string|Date} date - Date to extract components from
 * @returns {{year: number, month: number}} Object with year and month (0-11)
 *
 * @example
 * getAccountingYearMonth('2025-01-15'); // { year: 2025, month: 0 }
 */
export function getAccountingYearMonth(date) {
    const d = dayjs.utc(date);
    return {
        year: d.year(),
        month: d.month() // 0-11
    };
}

const dateUtils = {
    serializeTaskDateOnly,
    serializeTaskDateTime,
    formatAccountingDate,
    formatAccountingDateISO,
    parseAccountingDate,
    isSameAccountingDate,
    getMonthStartUTC,
    getMonthEndUTC,
    compareAccountingDates,
    getAccountingMonthKey,
    getAccountingYearMonth
};

export default dateUtils;
